import classNames from 'classnames';
import styled from 'styled-components';

import { useIntlContext } from '#src/contexts/intl/index.ts';
import { unitMapping } from '#src/lib/style.ts';
import type { Color } from '#src/style/types.ts';

type StatsType = {
  percent: number;
  color: Color;
}[];

const Root = styled.div`
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
  border-radius: var(--half-unit);
  background-color: var(--c-nd-100);
  :root.light-mode & {
    background-color: var(--c-nd-600);
  }
  gap: 1px;
  padding: var(--half-unit);
  &[role='button']:hover {
    cursor: pointer;
  }

  &.outlined {
    outline: 1px solid var(--c-nd-200);
  }
  &.transparent {
    background-color: transparent;
  }
  &.dark {
    background-color: var(--c-nl-500);
  }
`;

type Props = {
  stats: StatsType;
  outlined?: boolean;
  backgroundColor?: 'default' | 'transparent' | 'dark';
  onClick?: (e?: React.MouseEvent) => void;
  width?: keyof typeof unitMapping;
  height?: keyof typeof unitMapping;
};

export const StatsIcon = ({
  stats,
  onClick,
  outlined,
  backgroundColor = 'default',
  width = 4,
  height = 3,
}: Props) => {
  const { formatMessage } = useIntlContext();
  return (
    <Root
      aria-label={formatMessage(
        {
          defaultMessage: 'Last {count} stats',
        },
        { count: stats.length }
      )}
      onClick={onClick}
      role={onClick ? 'button' : 'img'}
      className={classNames({
        outlined,
        transparent: backgroundColor === 'transparent',
        dark: backgroundColor === 'dark',
      })}
      style={{
        width: unitMapping[width],
        height: unitMapping[height],
      }}
    >
      {stats.map(({ percent, color }, i) => {
        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            style={{
              flex: 1,
              height: `${percent * 100}%`,
              borderRadius: '2em',
              background: color,
            }}
            aria-label="stat-icon-rect"
          />
        );
      })}
    </Root>
  );
};
